import { all, takeEvery, takeLatest, put, call, select, cancelled } from 'redux-saga/effects';
import queryString from 'query-string';
import FileSaver from 'file-saver';
import { AXIOS_CANCEL_CODE } from '../../config/axios';
import { getFileNameFromApiResponseHeader } from '../../helpers/utility';
import { selectRisksActiveFilters } from '../filters/selectors';
import {
  getRisksHousingStocksFilterOptions,
  getRisksOrganizationsFilterOptions,
  getRisksCompanyCodesFilterOptions,
  getRisksCompanyLabelsFilterOptions,
  getRisksEconomicUnitsFilterOptions
} from '../filters/slice';
import {
  getRisks,
  getRisksSucceeded,
  getRisksFailed,
  searchRisks,
  searchRisksSucceeded,
  searchRisksFailed,
  exportRisks,
  exportRisksSucceeded,
  exportRisksFailed,
  reloadRisks
} from '../risks/slice';
import { onGetRisks, onExportRisks } from './api';

export function* getRisksWorker({ payload }) {
  const controller = new AbortController();

  try {
    const response = yield call(onGetRisks, payload, controller.signal);

    const result = {
      risks: response.data,
      total: Number(response.headers.total) || 0
    };

    yield put(getRisksSucceeded(result));
  } catch (error) {
    if (error?.code !== AXIOS_CANCEL_CODE) {
      yield put(getRisksFailed());
    }
  } finally {
    if (yield cancelled()) {
      controller.abort('Search cancelled by takeLatest');
    }
  }
}

export function* searchRisksWorker({ payload }) {
  const controller = new AbortController();

  try {
    const params = {
      ...payload,
      paging: false
    };
    const response = yield call(onGetRisks, params, controller.signal);
    yield put(searchRisksSucceeded(response.data));
  } catch (error) {
    if (error?.code !== AXIOS_CANCEL_CODE) {
      yield put(searchRisksFailed());
    }
  } finally {
    if (yield cancelled()) {
      controller.abort('Search cancelled by takeLatest');
    }
  }
}

export function* exportRisksWorker({ payload }) {
  const { params } = payload;
  try {
    const getRisksResponse = yield call(onGetRisks, { ...params, paging: false });
    const risksIds = getRisksResponse.data.map((risk) => risk.id);
    const response = yield call(onExportRisks, risksIds);
    const filename = getFileNameFromApiResponseHeader(response.headers, '.xlsx');

    // only IE does support this method
    if (window.navigator.msSaveOrOpenBlob) {
      const blob = new Blob([response.data], { type: 'application/pdf' });
      window.navigator.msSaveBlob(blob, filename);
    } else {
      const blob = new File([response.data], filename, {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64'
      });
      FileSaver.saveAs(blob);
    }

    yield put(exportRisksSucceeded());
  } catch {
    yield put(exportRisksFailed());
  }
}

/**
 * re-fetches the risks after creating a new one
 * the update must respect the current filters
 */
export function* reloadRisksWorker() {
  const filters = yield select(selectRisksActiveFilters);
  const params = queryString.parse(filters);
  yield all([
    put(getRisks(params)),
    // The filter options should be fetched on every risks reload,
    // as creating or editing risks might affect the filter options.
    put(getRisksHousingStocksFilterOptions()),
    put(getRisksOrganizationsFilterOptions()),
    put(getRisksCompanyCodesFilterOptions()),
    put(getRisksCompanyLabelsFilterOptions()),
    put(getRisksEconomicUnitsFilterOptions())
  ]);
}

export default function* risksSaga() {
  yield all([
    takeLatest(getRisks.type, getRisksWorker),
    takeLatest(searchRisks.type, searchRisksWorker),
    takeEvery(exportRisks.type, exportRisksWorker),
    takeLatest(reloadRisks.type, reloadRisksWorker)
  ]);
}
